/** @jsxImportSource @emotion/react */
import { createStyles, Typography, useTheme } from '@quickbit/qb-design-system';
import { useState } from 'react';
import { ChatMessageType } from 'api/useAgentChatApi';
import { Markdown } from 'components';

interface ChatMessageProps {
  eventType: ChatMessageType;
  content: string;
  timestamp: string;
}

export const ChatMessage = ({
  eventType,
  content,
  timestamp,
}: ChatMessageProps) => {
  const styles = useStyles();
  const { palette } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const isUser = eventType === 'USER_MESSAGE';

  if (eventType === 'TOOL_OUTPUT') {
    return (
      <div css={styles.toolOutputContainer}>
        <button
          css={styles.toolOutputButton}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Typography variant="caption">
            {isExpanded ? '▼' : '▶'} Tool Output
          </Typography>
        </button>
        {isExpanded && (
          <div css={styles.toolOutputContent}>
            <Markdown markdown={content} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      css={[
        styles.container,
        isUser ? styles.userMessageContainer : styles.assistantMessage,
      ]}
    >
      <div css={styles.header}>
        <span css={{ fontWeight: 600 }}>
          <Typography variant="caption">{isUser ? 'You' : 'Agent'}</Typography>
        </span>
        <span css={{ color: palette.grey.x400 }}>
          <Typography variant="caption">
            {new Date(timestamp).toLocaleTimeString()}
          </Typography>
        </span>
      </div>
      <div css={[isUser ? styles.userMessage : {}]}>
        <Markdown markdown={content} />
      </div>
    </div>
  );
};

const useStyles = createStyles(({ spacing, palette }) => ({
  container: {
    maxWidth: '80%',
  },
  userMessageContainer: {
    alignSelf: 'flex-end' as const,
  },
  userMessage: {
    borderRadius: spacing.xxxs,
    backgroundColor: palette.primary.x50,
    padding: `${spacing.xxxs} ${spacing.xs}`,
  },
  assistantMessage: {
    alignSelf: 'flex-start' as const,
    backgroundColor: palette.grey.x50,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xxxs,
    gap: spacing.xs,
  },
  toolOutputContainer: {
    alignSelf: 'flex-start' as const,
    maxWidth: '80%',
    marginTop: `calc(-1 * ${spacing.s})`,
  },
  toolOutputButton: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xxxs,
    padding: `${spacing.xxxs} 0`,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: palette.grey.x500,
    '&:hover': {
      color: palette.grey.x600,
    },
  },
  toolOutputContent: {
    padding: spacing.xxxs,
    backgroundColor: palette.white,
    borderRadius: spacing.xxxs,
    overflow: 'auto',
    maxHeight: 300,
  },
}));
